import React, { useState } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { assistApi } from './assistApi';

export const Field = ({ label, children, hint }) => (
  <label className="block text-xs text-gray-600">
    <span className="font-semibold">{label}</span>
    <div className="mt-1">{children}</div>
    {hint && <span className="text-[11px] text-gray-400">{hint}</span>}
  </label>
);

export const SaveBar = ({ busy, label = 'Save for installer', testId }) => (
  <div className="flex justify-end pt-1">
    <Button type="submit" size="sm" disabled={busy} className="bg-[#7A0B7A] hover:bg-[#5a085a]" data-testid={testId}>{busy ? 'Saving…' : label}</Button>
  </div>
);

export function useSave(fn, onSaved, okMsg) {
  const [busy, setBusy] = useState(false);
  const run = async (...args) => {
    setBusy(true);
    try { const d = await fn(...args); onSaved(d); toast.success(okMsg); } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };
  return [busy, run];
}

export function Stage2Form({ userId, brand, data, notify, onSaved }) {
  const [f, setF] = useState({
    company_name: data.company_name || '', contact_name: data.contact_name || '', mobile: data.mobile || '', email: data.email || '',
    business_address: data.business_address || '', postcode: data.postcode || '', coverage_radius_miles: data.coverage_radius_miles || '', areas_covered: data.areas_covered || '',
  });
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const [busy, save] = useSave(() => assistApi.stage2(userId, brand, { ...f, coverage_radius_miles: f.coverage_radius_miles ? Number(f.coverage_radius_miles) : null, notify }), onSaved, 'Company profile saved');
  return (
    <form onSubmit={(e) => { e.preventDefault(); save(); }} className="grid sm:grid-cols-2 gap-3" data-testid="assist-stage2-form">
      <Field label="Company name"><Input value={f.company_name} onChange={set('company_name')} data-testid="assist-s2-company" /></Field>
      <Field label="Contact name"><Input value={f.contact_name} onChange={set('contact_name')} /></Field>
      <Field label="Mobile"><Input value={f.mobile} onChange={set('mobile')} /></Field>
      <Field label="Business email"><Input type="email" value={f.email} onChange={set('email')} /></Field>
      <div className="sm:col-span-2"><Field label="Business address"><Input value={f.business_address} onChange={set('business_address')} /></Field></div>
      <Field label="Postcode" hint="Needed for the map and lead matching"><Input value={f.postcode} onChange={set('postcode')} data-testid="assist-s2-postcode" /></Field>
      <Field label="Travel radius (miles)"><Input type="number" min="1" value={f.coverage_radius_miles} onChange={set('coverage_radius_miles')} /></Field>
      <div className="sm:col-span-2"><Field label="Areas covered"><Input value={f.areas_covered} onChange={set('areas_covered')} placeholder="e.g. Derby, Nottingham, Leicester" /></Field></div>
      <div className="sm:col-span-2"><SaveBar busy={busy} testId="assist-s2-save" /></div>
    </form>
  );
}

export function Stage3Form({ userId, brand, data, notify, onSaved }) {
  const keys = ['website', 'facebook', 'instagram', 'tiktok', 'linkedin'];
  const [f, setF] = useState(Object.fromEntries(keys.map((k) => [k, data[k] || ''])));
  const [busy, save] = useSave(() => assistApi.stage3(userId, brand, { ...f, notify }), onSaved, 'Online presence saved & marked complete');
  return (
    <form onSubmit={(e) => { e.preventDefault(); save(); }} className="grid sm:grid-cols-2 gap-3" data-testid="assist-stage3-form">
      {keys.map((k) => (
        <Field key={k} label={k === 'website' ? 'Website' : k.charAt(0).toUpperCase() + k.slice(1)}>
          <Input value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} placeholder="https://" data-testid={`assist-s3-${k}`} />
        </Field>
      ))}
      <div className="sm:col-span-2 flex items-center justify-between gap-3">
        <span className="text-[11px] text-gray-500">Saving marks this stage complete — you've checked the links.</span>
        <SaveBar busy={busy} testId="assist-s3-save" />
      </div>
    </form>
  );
}

export function Stage4Form({ userId, brand, data, notify, onSaved }) {
  const [f, setF] = useState({ insurance_company: data.insurance_company || '', policy_number: data.policy_number || '', expiry_date: data.expiry_date || '' });
  const [file, setFile] = useState(null);
  const [busy, save] = useSave(() => {
    const fd = new FormData();
    Object.entries(f).forEach(([k, v]) => fd.append(k, v));
    fd.append('notify', notify ? 'true' : 'false');
    if (file) fd.append('file', file);
    return assistApi.stage4(userId, brand, fd);
  }, onSaved, 'Insurance saved');
  return (
    <form onSubmit={(e) => { e.preventDefault(); save(); }} className="grid sm:grid-cols-3 gap-3" data-testid="assist-stage4-form">
      <Field label="Insurer"><Input value={f.insurance_company} onChange={(e) => setF({ ...f, insurance_company: e.target.value })} data-testid="assist-s4-insurer" /></Field>
      <Field label="Policy number"><Input value={f.policy_number} onChange={(e) => setF({ ...f, policy_number: e.target.value })} /></Field>
      <Field label="Expiry date"><Input type="date" value={f.expiry_date} onChange={(e) => setF({ ...f, expiry_date: e.target.value })} data-testid="assist-s4-expiry" /></Field>
      <div className="sm:col-span-3">
        <Field label="Certificate (PDF / image, optional)" hint={data.certificate_filename ? `Current: ${data.certificate_filename}` : 'No certificate on file yet'}>
          <div className="flex items-center gap-2">
            <input type="file" accept=".pdf,.png,.jpg,.jpeg,.webp" onChange={(e) => setFile(e.target.files?.[0] || null)} className="text-xs" data-testid="assist-s4-file" />
            {data.certificate_gridfs_id && <a className="text-xs text-[#2F5E96] underline" href={assistApi.certUrl(userId, brand)} target="_blank" rel="noreferrer">View current</a>}
          </div>
        </Field>
      </div>
      <div className="sm:col-span-3 flex items-center justify-between gap-3">
        <span className="text-[11px] text-gray-500">Insurer + expiry date mark the stage complete; the certificate can follow later.</span>
        <SaveBar busy={busy} testId="assist-s4-save" />
      </div>
    </form>
  );
}

export function NotesBox({ value, onChange, placeholder }) {
  return <Textarea value={value} onChange={(e) => onChange(e.target.value)} rows={2} placeholder={placeholder} className="text-sm" />;
}
